/**
 * PlanGeneratorButton — Coach-suggested pre-market plan, on demand.
 *
 * Renders a small "✨ Generate" button in the Today's Plan header. When
 * clicked, gathers the trader's profile + recent regime + today's events
 * into a plan context, asks the LLM for a 3-5 sentence forward-looking
 * plan, and shows the suggestion in a small modal-like card with two
 * actions:
 *
 *   - "Use this plan" → writes to journalEntry.plan via the supplied
 *     onAcceptPlan(text) callback. Replaces whatever was there.
 *   - "Append to existing" → appends with a separator, preserving any
 *     manual notes the trader already wrote.
 *   - "Discard" → close + forget.
 *
 * Hidden entirely when the LLM narrator isn't ready (no key / disabled).
 *
 * Props:
 *   profile           current CoachProfile (from useCoachProfile)
 *   recentTrades      closed trades from the last 7 days
 *   todayEvents       today's news events array
 *   dateStr           the trading day key (YYYY-MM-DD)
 *   priorAdherence    optional: yesterday's adherence headline
 *   apiKey            from coachLLMSettings
 *   model             from coachLLMSettings
 *   voice             from coachLLMSettings
 *   ttsEnabled        from coachLLMSettings (powers the speak button on the suggestion)
 *   onAcceptPlan      (text, opts) => void — { append: bool }
 *   currentPlan       string — used by the "append" path
 *   darkMode
 */

import { useState, useRef, useEffect } from 'react';
import { Sparkles, Loader2, X, Check, Plus } from 'lucide-react';
import { buildPlanContext, generatePlan } from '../../services/coach/plan/buildPlanSuggestion';
import CoachSpeakButton from './CoachSpeakButton';

const PlanGeneratorButton = ({
  profile,
  recentTrades = [],
  todayEvents = [],
  dateStr = '',
  priorAdherence = null,
  apiKey = '',
  model = '',
  voice = 'mentor',
  ttsEnabled = false,
  // Phase 16 — TTS provider config (settings.coachLLM.tts). Threaded through
  // to CoachSpeakButton so cloud TTS providers use the right API key.
  ttsSettings = null,
  onAcceptPlan,
  currentPlan = '',
  getTradeNetPnL,
  getAccountById,
  darkMode = true,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState('');
  const inflightRef = useRef(null);

  const isReady = !!(apiKey && profile);

  // Cancel any in-flight request on unmount.
  useEffect(() => () => {
    if (inflightRef.current) {
      try { inflightRef.current.abort(); } catch { /* */ }
    }
  }, []);

  const handleGenerate = async () => {
    if (loading) return;
    setOpen(true);
    setLoading(true);
    setError(null);
    setSuggestion('');

    // Cancel any prior request and start a new one
    if (inflightRef.current) {
      try { inflightRef.current.abort(); } catch { /* */ }
    }
    const controller = new AbortController();
    inflightRef.current = controller;

    const planContext = buildPlanContext({
      profile,
      recentTrades,
      todayEvents,
      dateStr,
      priorAdherence,
      getTradeNetPnL,
      getAccountById,
    });
    const result = await generatePlan({
      planContext, apiKey, model, voice, signal: controller.signal,
    });
    if (controller !== inflightRef.current) return;  // a newer request superseded
    setLoading(false);
    if (result.ok) {
      setSuggestion(result.text);
    } else {
      setError(result.error || 'Plan generation failed');
    }
  };

  const handleAccept = (append = false) => {
    if (!suggestion) return;
    if (typeof onAcceptPlan === 'function') {
      onAcceptPlan(suggestion, { append, currentPlan });
    }
    setOpen(false);
    setSuggestion('');
  };

  const handleDiscard = () => {
    if (inflightRef.current) {
      try { inflightRef.current.abort(); } catch { /* */ }
    }
    setOpen(false);
    setSuggestion('');
    setError(null);
    setLoading(false);
  };

  // Hide the button entirely when LLM isn't ready — keeps the existing
  // plan-edit UI uncluttered for traders who don't use the LLM narrator.
  if (!isReady) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleGenerate}
        disabled={loading}
        title="Generate today's plan from your Coach Profile + recent regime + today's events"
        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ${
          darkMode
            ? 'bg-purple-500/15 border-purple-400/30 text-purple-200 hover:bg-purple-500/25'
            : 'bg-purple-100/70 border-purple-300/60 text-purple-700 hover:bg-purple-200'
        } ${loading ? 'opacity-60 cursor-wait' : ''}`}
      >
        {loading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
        {loading ? 'Generating…' : 'Generate'}
      </button>

      {/* Inline suggestion panel — sits below the plan header when open */}
      {open && (
        <div className={`mt-2 rounded-lg border p-3 ${
          darkMode
            ? 'bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/25'
            : 'bg-gradient-to-br from-purple-50 to-white border-purple-200'
        }`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5">
              <Sparkles size={12} className={darkMode ? 'text-purple-300' : 'text-purple-700'} />
              <span className={`text-[11px] uppercase tracking-wider font-semibold ${darkMode ? 'text-purple-200' : 'text-purple-800'}`}>
                Coach-Suggested Plan
              </span>
              {suggestion && (
                <CoachSpeakButton
                  text={suggestion}
                  voice={voice}
                  ttsEnabled={ttsEnabled}
                  ttsSettings={ttsSettings}
                  darkMode={darkMode}
                />
              )}
            </div>
            <button
              type="button"
              onClick={handleDiscard}
              className={`p-1 rounded ${darkMode ? 'text-gray-400 hover:bg-white/5' : 'text-gray-500 hover:bg-gray-100'}`}
              aria-label="Close"
            >
              <X size={11} />
            </button>
          </div>

          {loading && (
            <div className="space-y-1.5">
              <div className={`h-3 w-full rounded ${darkMode ? 'bg-white/5' : 'bg-gray-200'} animate-pulse`} />
              <div className={`h-3 w-11/12 rounded ${darkMode ? 'bg-white/5' : 'bg-gray-200'} animate-pulse`} />
              <div className={`h-3 w-9/12 rounded ${darkMode ? 'bg-white/5' : 'bg-gray-200'} animate-pulse`} />
            </div>
          )}
          {!loading && suggestion && (
            <>
              <p className={`text-sm leading-relaxed mb-3 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                {suggestion}
              </p>
              <div className="flex items-center justify-end gap-1.5">
                {currentPlan && currentPlan.trim().length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleAccept(true)}
                    className={`text-[11px] px-2 py-1 rounded-md border inline-flex items-center gap-1 ${
                      darkMode ? 'bg-white/5 border-white/10 text-gray-200 hover:bg-white/10' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Plus size={10} />
                    Append to existing
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleAccept(false)}
                  className={`text-[11px] px-2.5 py-1 rounded-md font-medium inline-flex items-center gap-1 ${
                    darkMode ? 'bg-purple-500/30 border border-purple-400/50 text-purple-100 hover:bg-purple-500/40' : 'bg-purple-100 border border-purple-300 text-purple-800 hover:bg-purple-200'
                  }`}
                >
                  <Check size={10} />
                  {currentPlan && currentPlan.trim().length > 0 ? 'Replace plan' : 'Use this plan'}
                </button>
              </div>
            </>
          )}
          {!loading && error && (
            <p className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
              Plan generation failed: {error}
            </p>
          )}
        </div>
      )}
    </>
  );
};

export default PlanGeneratorButton;
